import type {
  InsertOrganizationPayload,
  InsertOrganizationResponse,
} from '@/graphql/mutations/organizations/use-insert-organization';
import type { MyOrganizationResponse } from '../use-my-ognization/types';
import type { CreateOrganizationPayload, CreateOrganizationResponse } from './types';
import { isOrganizationNameAlreadyExists } from '.';
import {
  registerSignatureOrganization,
  registerOrganizationWebhook,
} from '@/modules/templates/services/signature.service';

type StepKey = 'logo' | 'iam' | 'signature' | 'database' | 'membership' | 'webhook';

export interface OrganizationSetupStep {
  key: StepKey;
  label: string;
  status: 'pending' | 'active' | 'done' | 'error';
}

export interface OrganizationSetupInput {
  name: string;
  signatureOrgId?: string;
  uploadLogo?: () => Promise<string | null>;
  user: {
    itemId: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
  };
}

interface OrganizationSetupActions {
  createOrganization: (input: CreateOrganizationPayload) => Promise<CreateOrganizationResponse>;
  insertOrganization: (input: InsertOrganizationPayload) => Promise<InsertOrganizationResponse>;
  getOrganizations: () => Promise<MyOrganizationResponse>;
  onProgress: (steps: OrganizationSetupStep[]) => void;
}

/** Run the shared creation/linking steps using the mutations supplied by the page. */
export async function setupOrganization(
  input: OrganizationSetupInput,
  actions: OrganizationSetupActions
) {
  const name = input.name.trim();
  if (!name || !input.user.itemId) throw new Error('A name and signed-in user are required.');

  let steps: OrganizationSetupStep[] = [
    ...(input.uploadLogo
      ? [{ key: 'logo' as const, label: 'Uploading logo', status: 'pending' as const }]
      : []),
    { key: 'iam', label: 'Creating organization', status: 'pending' },
    ...(!input.signatureOrgId
      ? [{ key: 'signature' as const, label: 'Setting up e-signing', status: 'pending' as const }]
      : []),
    {
      key: 'database',
      label: input.signatureOrgId
        ? 'Linking signature organization'
        : 'Setting up your organization',
      status: 'pending',
    },
    { key: 'membership', label: 'Checking your access', status: 'pending' },
    { key: 'webhook', label: 'Configuring live document events', status: 'pending' },
  ];
  actions.onProgress(steps);

  const runStep = async <T>(key: StepKey, action: () => Promise<T>): Promise<T> => {
    const update = (status: OrganizationSetupStep['status']) => {
      steps = steps.map((step) => (step.key === key ? { ...step, status } : step));
      actions.onProgress(steps);
    };
    update('active');
    try {
      const result = await action();
      update('done');
      return result;
    } catch (error) {
      update('error');
      throw error;
    }
  };

  const getOrganizations = async () => {
    const response = await actions.getOrganizations();
    if (response.isSuccess === false) throw new Error('Unable to read your organizations.');
    return response.organizations ?? [];
  };

  const logoUrl = input.uploadLogo
    ? await runStep('logo', async () => {
        const url = await input.uploadLogo?.();
        if (!url) throw new Error('Failed to upload logo.');
        return url;
      })
    : '';

  const blocksOrgId = await runStep('iam', async () => {
    const existing = (await getOrganizations()).find((org) => org.name === name);
    if (existing?.itemId) return existing.itemId;

    const result = await actions.createOrganization({
      name,
      description: '',
      defaultRoleForMembers: ['org_admin'],
      defaultPermissionsForMembers: [],
      email: input.user.email,
      phoneNumber: input.user.phoneNumber,
      websiteUrl: '',
      addresses: [],
      attributes: { additionalProp1: '', additionalProp2: '', additionalProp3: '' },
      createdFrom: 3,
    });
    if (result.isSuccess && result.itemId) return result.itemId;
    if (!result.isSuccess && !isOrganizationNameAlreadyExists(result)) {
      throw new Error(
        Object.values(result.errors ?? {}).find(Boolean) || 'Failed to create organization.'
      );
    }

    const resolved = (await getOrganizations()).find((org) => org.name === name);
    if (!resolved?.itemId) {
      throw new Error(
        'Unable to find this organization in your account. Please refresh and try again.'
      );
    }
    return resolved.itemId;
  });

  const signatureOrgId =
    input.signatureOrgId ??
    (await runStep('signature', () =>
      registerSignatureOrganization({
        firstName: input.user.firstName,
        lastName: input.user.lastName,
        email: input.user.email,
        name,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
    ));
  const slug = `${name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')}-${crypto.randomUUID()}`;
  const organization = await runStep('database', async () => {
    const response = await actions.insertOrganization({
      BlocksOrgId: blocksOrgId,
      SigOrgId: signatureOrgId,
      Name: name,
      Slug: slug,
      LogoUrl: logoUrl,
      MaxUsers: 10,
      MaxTemplates: 50,
      SeliseClientId: '',
      SeliseClientSecret: '',
    });
    const result = response.insertOrganizations;
    if (!result?.acknowledged || !result.itemId) {
      throw new Error(result?.message || 'Failed to save organization.');
    }
    return {
      BlocksOrgId: blocksOrgId,
      SigOrgId: signatureOrgId,
      Slug: slug,
      ItemId: result.itemId,
    };
  });

  await runStep('membership', async () => {
    const organizations = await getOrganizations();
    if (!organizations.some((org) => org.itemId === blocksOrgId)) {
      throw new Error(
        'Your organization access is not available yet. Please refresh and try again.'
      );
    }
  });
  await runStep('webhook', () => registerOrganizationWebhook(signatureOrgId, input.user.email));
  return organization;
}
